import { Badge, Button, Card, CardHeader, Drawer, EmptyState, Table, useToast } from "@helmr/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { createIntakeKey, createPaymentAccount, listIntakeKeys, listLeadStages, listPaymentAccounts, patchPaymentAccount, putLeadStages, revokeIntakeKey,
  type LeadStage, type PaymentAccount } from "../../api/settings";
import { listKiosks, renameKiosk, revokeKiosk, rotateCheckinKey, type Kiosk } from "../../api/devices";
import { getHealth, listConnections, listTemplates } from "../../api/messaging";
import { ConfirmDrawer, fmtDateTime, Field, Load, mapError, Page, SecretDrawer, TableSkeleton, useDirtyGuard, useScope, SavedBar, type Mapped } from "./kit";
import { MessagingSettingsForm, useBranchOptions } from "./SectionPages";
import s from "./settings.module.css";
import { section } from "./sections";
import { t } from "./strings";

/* ---------- payment accounts (test stub only) ---------- */

export function PaymentAccountsPage() {
  const sc = useScope();
  const toast = useToast();
  const q = useQuery({ queryKey: sc.key("payment-accounts"), queryFn: () => listPaymentAccounts(sc.client), retry: false });
  const canEdit = sc.ctx.has("settings.payment_accounts_edit");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PaymentAccount | null>(null);
  const toggle = useMutation({
    mutationFn: (a: PaymentAccount) => patchPaymentAccount(sc.client, a.id, { status: a.status === "active" ? "disabled" : "active" }),
    onSuccess: async () => { await sc.qc.invalidateQueries({ queryKey: sc.key("payment-accounts") }); toast.show(t.saved); },
    onError: (e) => toast.show(mapError(e, []).form ?? "Could not update"),
  });
  return (
    <Page title={t.sections.paymentAccounts.title} description={t.sections.paymentAccounts.body} allow={section("payment-accounts")}
      actions={canEdit ? <Button variant="primary" onClick={() => setAdding(true)}>Add test account</Button> : undefined}>
      <div className={s.note} style={{ margin: 0 }}>This build creates accounts on the test stub provider only. Live provider credentials are never typed here.</div>
      <Card aria-label="Payment accounts">
        <CardHeader title="Accounts" />
        <Load query={q} what="payment accounts" skeleton={<TableSkeleton rows={3} />}>
          {(list) => list.length === 0
            ? <EmptyState compact icon="wallet" title="No payment accounts yet" description="Add the account online payments are received in." action={canEdit ? <Button onClick={() => setAdding(true)}>Add test account</Button> : undefined} />
            : <Table label="Payment accounts" rows={list} rowKey={(a) => a.id}
              columns={[
                { key: "l", header: "Label", cell: (a) => <span className={s.name} title={a.label}>{a.label}</span> },
                { key: "p", header: "Provider", cell: (a) => a.provider },
                { key: "m", header: "Mode", cell: (a) => <Badge tone={a.mode === "live" ? "warn" : "neutral"}>{a.mode}</Badge> },
                { key: "s", header: "Status", cell: (a) => <Badge tone={a.status === "active" ? "ok" : "neutral"}>{a.status}</Badge> },
                { key: "x", header: "Actions", srOnly: true, align: "end", cell: (a) => canEdit ? <span className={s.rowActions}><Button size="sm" onClick={() => setEditing(a)} aria-label={`Rename ${a.label}`}>Rename</Button><Button size="sm" onClick={() => toggle.mutate(a)} aria-label={`${a.status === "active" ? "Disable" : "Enable"} ${a.label}`}>{a.status === "active" ? "Disable" : "Enable"}</Button></span> : null },
              ]} />}
        </Load>
      </Card>
      <AccountDrawer open={adding || editing !== null} account={editing} onClose={() => { setAdding(false); setEditing(null); }} />
    </Page>
  );
}

function randomHex(bytes: number) {
  return Array.from(globalThis.crypto.getRandomValues(new Uint8Array(bytes)), (b) => b.toString(16).padStart(2, "0")).join("");
}

function AccountDrawer({ open, account, onClose }: { open: boolean; account: PaymentAccount | null; onClose: () => void }) {
  const sc = useScope();
  const toast = useToast();
  const [label, setLabel] = useState("");
  const [err, setErr] = useState<Mapped>({ fields: {}, form: null });
  const [seen, setSeen] = useState<unknown>(undefined);
  const key = open ? (account?.id ?? "new") : null;
  if (seen !== key) { setSeen(key); setLabel(account?.label ?? ""); setErr({ fields: {}, form: null }); }
  const m = useMutation({
    // The stub provider needs no real credential: placeholders and a random webhook secret are generated here and never shown.
    mutationFn: () => (account ? patchPaymentAccount(sc.client, account.id, { label: label.trim() }) : createPaymentAccount(sc.client, { provider: "stub", label: label.trim(), mode: "test", credentials: { apiKey: "stub" }, webhookSecret: randomHex(16) })),
    onSuccess: async () => { await sc.qc.invalidateQueries({ queryKey: sc.key("payment-accounts") }); toast.show(t.saved); onClose(); },
    onError: (e) => setErr(mapError(e, ["label"])),
  });
  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={account ? `Rename ${account.label}` : "Add test account"} description={account ? undefined : "Test mode, stub provider. No real money moves."}
      footer={<><Button onClick={onClose}>{t.cancel}</Button><Button variant="primary" busy={m.isPending} disabled={!label.trim()} onClick={() => m.mutate()}>{account ? t.save : "Add account"}</Button></>}>
      <div className={s.stack}>
        <Field label="Label" error={err.fields.label} htmlFor="pa-label"><input id="pa-label" className={s.input} value={label} onChange={(e) => setLabel(e.target.value)} /></Field>
        {err.form ? <div className={s.formErr} role="alert" style={{ margin: 0 }}>{err.form}</div> : null}
      </div>
    </Drawer>
  );
}

/* ---------- lead stages ---------- */

type StageRow = { id?: string; name: string; type: LeadStage["type"] };

export function LeadStagesPage() {
  const sc = useScope();
  const q = useQuery({ queryKey: sc.key("lead-stages"), queryFn: () => listLeadStages(sc.client), retry: false });
  return (
    <Page title={t.sections.leadStages.title} description={t.sections.leadStages.body} allow={section("lead-stages")}>
      <Card aria-label="Lead stages">
        <CardHeader title="Pipeline" subtitle="Leads move left to right. A stage is open, won or lost." />
        <Load query={q} what="lead stages" skeleton={<TableSkeleton rows={5} />}>{(stages) => <StagesEditor stages={stages} />}</Load>
      </Card>
    </Page>
  );
}

function StagesEditor({ stages }: { stages: LeadStage[] }) {
  const sc = useScope();
  const toast = useToast();
  const canEdit = sc.ctx.has("settings.leads_setup_edit");
  const [rows, setRows] = useState<StageRow[]>(stages);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [err, setErr] = useState<Mapped>({ fields: {}, form: null });
  const [saved, setSaved] = useState(stages);
  const dirty = JSON.stringify(rows) !== JSON.stringify(saved) || Object.keys(mapping).length > 0;
  const { node: guard, release } = useDirtyGuard(dirty);
  const removed = saved.filter((o) => !rows.some((r) => r.id === o.id));
  const move = (i: number, d: -1 | 1) => setRows((r) => { const n = [...r]; const j = i + d; if (j < 0 || j >= n.length) return r; [n[i], n[j]] = [n[j]!, n[i]!]; return n; });
  const m = useMutation({
    mutationFn: () => putLeadStages(sc.client, { stages: rows, ...(Object.keys(mapping).length ? { mapping } : {}) }),
    onSuccess: async (r) => { release(); sc.qc.setQueryData(sc.key("lead-stages"), r.stages); setRows(r.stages); setSaved(r.stages); setMapping({}); setErr({ fields: {}, form: null }); toast.show(r.moved ? `Saved. ${r.moved} lead${r.moved === 1 ? "" : "s"} moved` : t.saved); },
    onError: (e) => setErr(mapError(e, [])),
  });
  return (
    <form className={s.form} onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
      {guard}
      <div className={s.list}>
        {rows.map((r, i) => (
          <div key={r.id ?? `new-${i}`} className={s.stageRow}>
            <input className={s.input} aria-label={`Stage ${i + 1} name`} value={r.name} disabled={!canEdit} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
            <select className={s.select} aria-label={`Stage ${i + 1} type`} value={r.type} disabled={!canEdit} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, type: e.target.value as LeadStage["type"] } : x)))}>
              <option value="open">Open</option><option value="won">Won</option><option value="lost">Lost</option>
            </select>
            {canEdit ? <span className={s.rowActions}>
              <Button size="sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${r.name} up`}>Up</Button>
              <Button size="sm" disabled={i === rows.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${r.name} down`}>Down</Button>
              <Button size="sm" disabled={rows.length <= 1} onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label={`Remove ${r.name}`}>Remove</Button>
            </span> : <span />}
          </div>
        ))}
      </div>
      {removed.length ? (
        <div className={s.pad}>
          <p className={s.hint}>Leads on a removed stage move to a stage of the same type. Choose where they go.</p>
          {removed.map((o) => (
            <Field key={o.id} label={`Move leads from “${o.name}” to`} htmlFor={`map-${o.id}`}>
              <select id={`map-${o.id}`} className={s.select} value={mapping[o.id] ?? ""} onChange={(e) => setMapping({ ...mapping, [o.id]: e.target.value })}>
                <option value="">Choose a stage…</option>
                {rows.filter((r) => r.id && r.type === o.type).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
          ))}
        </div>
      ) : null}
      {canEdit ? <div className={s.pad}><Button onClick={() => setRows([...rows, { name: "", type: "open" }])}>Add stage</Button></div> : null}
      {err.form ? <div className={s.formErr} role="alert">{err.form}</div> : null}
      <SavedBar dirty={dirty} busy={m.isPending} canEdit={canEdit} onDiscard={() => { setRows(saved); setMapping({}); setErr({ fields: {}, form: null }); }} />
    </form>
  );
}

/* ---------- intake keys: the secret is shown once ---------- */

export function IntakeKeysPage() {
  const sc = useScope();
  const toast = useToast();
  const q = useQuery({ queryKey: sc.key("intake-keys"), queryFn: () => listIntakeKeys(sc.client), retry: false });
  const canEdit = sc.ctx.has("settings.leads_setup_edit");
  const branches = useBranchOptions();
  const [creating, setCreating] = useState(false);
  // The one place a secret lives: component state, dropped on "I've saved it". Never the query cache, storage or a log.
  const [secret, setSecret] = useState<{ value: string; label: string; path: string } | null>(null);
  const [revoking, setRevoking] = useState<{ id: string; label: string } | null>(null);
  const [revokeErr, setRevokeErr] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: () => revokeIntakeKey(sc.client, revoking!.id),
    onSuccess: async () => { await sc.qc.invalidateQueries({ queryKey: sc.key("intake-keys") }); toast.show(`Revoked ${revoking!.label}`); setRevoking(null); setRevokeErr(null); },
    onError: (e) => setRevokeErr(mapError(e, []).form),
  });
  return (
    <Page title={t.sections.intakeKeys.title} description={t.sections.intakeKeys.body} allow={section("intake-keys")}
      actions={canEdit ? <Button variant="primary" onClick={() => setCreating(true)}>Create key</Button> : undefined}>
      <Card aria-label="Intake keys">
        <CardHeader title="Keys" />
        <Load query={q} what="intake keys" skeleton={<TableSkeleton rows={3} />}>
          {(list) => list.length === 0
            ? <EmptyState compact icon="inbox" title="No intake keys: create one to connect a lead form" description="A key lets a website or ad form post leads straight into Helmr." action={canEdit ? <Button onClick={() => setCreating(true)}>Create key</Button> : undefined} />
            : <Table label="Intake keys" rows={list} rowKey={(k) => k.id} minWidth={560}
              columns={[
                { key: "l", header: "Label", cell: (k) => <><span className={s.name} title={k.label}>{k.label}</span><span className={s.sub}>{k.webhookPath}</span></> },
                { key: "s", header: "Source", cell: (k) => k.defaultSource },
                { key: "u", header: "Last used", cell: (k) => (k.lastUsedAt ? fmtDateTime(k.lastUsedAt, sc.ctx.timeZone, sc.ctx.locale) : "Never") },
                { key: "t", header: "Status", cell: (k) => <Badge tone={k.status === "active" ? "ok" : "neutral"}>{k.status}</Badge> },
                { key: "x", header: "Actions", srOnly: true, align: "end", cell: (k) => canEdit && k.status === "active" ? <Button size="sm" onClick={() => setRevoking({ id: k.id, label: k.label })} aria-label={`Delete ${k.label}`}>Delete</Button> : null },
              ]} />}
        </Load>
      </Card>
      <CreateKeyDrawer open={creating} branches={branches} onClose={() => setCreating(false)}
        onCreated={(value, label, path) => { setCreating(false); setSecret({ value, label, path }); void sc.qc.invalidateQueries({ queryKey: sc.key("intake-keys") }); }} />
      <SecretDrawer open={secret !== null} secret={secret?.value ?? null} title="Your intake key" description={`Use it for ${secret?.label ?? "this key"}.`} label="Secret" onDone={() => setSecret(null)}
        extra={secret ? <p className={s.hint}>Post leads to <code>{secret.path}</code> with this secret.</p> : null} />
      <ConfirmDrawer open={revoking !== null} onClose={() => { setRevoking(null); setRevokeErr(null); }} title="Delete intake key" description={`${revoking?.label ?? ""} stops accepting leads at once.`}
        confirmLabel="Delete key" busyLabel="Deleting…" busy={revoke.isPending} error={revokeErr} onConfirm={() => revoke.mutate()} />
    </Page>
  );
}

function CreateKeyDrawer({ open, branches, onClose, onCreated }: { open: boolean; branches: { id: string; name: string }[]; onClose: () => void; onCreated: (secret: string, label: string, path: string) => void }) {
  const sc = useScope();
  const [label, setLabel] = useState("");
  const [source, setSource] = useState("");
  const [branch, setBranch] = useState("");
  const [err, setErr] = useState<Mapped>({ fields: {}, form: null });
  const m = useMutation({
    // The secret is handed to the parent from inside the mutation and never returned: a mutation's result stays in the
    // mutation cache for as long as this observer is mounted, and the secret must not.
    mutationFn: async () => {
      const r = await createIntakeKey(sc.client, { label: label.trim(), ...(source.trim() ? { defaultSource: source.trim() } : {}), ...(branch ? { defaultBranchId: branch } : {}) });
      onCreated(r.secret, r.key.label, r.key.webhookPath);
      return r.key;
    },
    onSuccess: () => { setLabel(""); setSource(""); setBranch(""); setErr({ fields: {}, form: null }); },
    onError: (e) => setErr(mapError(e, ["label", "defaultSource", "defaultBranchId"])),
  });
  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Create intake key" description="You will see the secret once."
      footer={<><Button onClick={onClose}>{t.cancel}</Button><Button variant="primary" busy={m.isPending} busyLabel="Creating…" disabled={!label.trim()} onClick={() => m.mutate()}>Create key</Button></>}>
      <div className={s.stack}>
        <Field label="Label" error={err.fields.label} htmlFor="ik-label" hint="Where it will be used, e.g. Website form."><input id="ik-label" className={s.input} value={label} onChange={(e) => setLabel(e.target.value)} /></Field>
        <Field label="Default source" error={err.fields.defaultSource} htmlFor="ik-src" hint="Leads without a source are tagged with this."><input id="ik-src" className={s.input} value={source} placeholder="webhook" onChange={(e) => setSource(e.target.value)} /></Field>
        <Field label="Default branch" error={err.fields.defaultBranchId} htmlFor="ik-br"><select id="ik-br" className={s.select} value={branch} onChange={(e) => setBranch(e.target.value)}><option value="">None</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></Field>
        {err.form ? <div className={s.formErr} role="alert" style={{ margin: 0 }}>{err.form}</div> : null}
      </div>
    </Drawer>
  );
}

/* ---------- devices: kiosks and the check-in key ---------- */

export function DevicesPage() {
  const sc = useScope();
  const toast = useToast();
  const q = useQuery({ queryKey: sc.key("kiosks"), queryFn: () => listKiosks(sc.client), retry: false });
  const branches = useBranchOptions();
  const canRename = sc.ctx.has("devices.kiosks_edit");
  const canRotate = sc.ctx.has("devices.checkin_keys");
  const canRevoke = sc.ctx.has("devices.kiosk_revoke");
  const [renaming, setRenaming] = useState<Kiosk | null>(null);
  const [label, setLabel] = useState("");
  const [renameErr, setRenameErr] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<Kiosk | null>(null);
  const [revokeErr, setRevokeErr] = useState<string | null>(null);
  const [rotate, setRotate] = useState(false);
  const [rotateErr, setRotateErr] = useState<string | null>(null);
  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? id;
  const refresh = () => sc.qc.invalidateQueries({ queryKey: sc.key("kiosks") });
  const rename = useMutation({ mutationFn: () => renameKiosk(sc.client, renaming!.id, label.trim()), onSuccess: async () => { await refresh(); toast.show(t.saved); setRenaming(null); }, onError: (e) => setRenameErr(mapError(e, []).form) });
  const revoke = useMutation({ mutationFn: () => revokeKiosk(sc.client, revoking!.id), onSuccess: async () => { await refresh(); toast.show(`Revoked ${revoking!.label}`); setRevoking(null); setRevokeErr(null); }, onError: (e) => setRevokeErr(mapError(e, []).form) });
  const rot = useMutation({ mutationFn: () => rotateCheckinKey(sc.client), onSuccess: () => { toast.show("Check-in key rotated"); setRotate(false); setRotateErr(null); }, onError: (e) => setRotateErr(mapError(e, []).form) });
  return (
    <Page title={t.sections.devices.title} description={t.sections.devices.body} allow={section("devices")}
      actions={<>{sc.ctx.has("devices.kiosks_add") ? <Button as={Link} to="/kiosk">Use this device as a check-in kiosk</Button> : null}{canRotate ? <Button onClick={() => setRotate(true)}>Rotate check-in key</Button> : null}</>}>
      <Card aria-label="Kiosks">
        <CardHeader title="Kiosks" subtitle="Tablets paired to a branch for QR check-in." />
        <Load query={q} what="kiosks" skeleton={<TableSkeleton rows={3} />}>
          {(list) => list.length === 0
            ? <EmptyState compact icon="finger" title="No kiosks paired" description="Pair a tablet from the tablet itself: open Helmr there and choose “Use this device as a check-in kiosk”." />
            : <Table label="Kiosks" rows={list} rowKey={(k) => k.id} minWidth={560}
              columns={[
                { key: "l", header: "Kiosk", cell: (k) => <><span className={s.name} title={k.label}>{k.label}</span><span className={s.sub}>{branchName(k.branchId)} · {k.mode}</span></> },
                { key: "s", header: "Status", cell: (k) => <Badge tone={k.status === "active" ? "ok" : "neutral"}>{k.status}</Badge> },
                { key: "u", header: "Last seen", cell: (k) => (k.lastSeenAt ? fmtDateTime(k.lastSeenAt, sc.ctx.timeZone, sc.ctx.locale) : "Never") },
                { key: "x", header: "Actions", srOnly: true, align: "end", cell: (k) => k.status === "active" ? <span className={s.rowActions}>
                  {canRename ? <Button size="sm" aria-label={`Rename ${k.label}`} onClick={() => { setRenaming(k); setLabel(k.label); setRenameErr(null); }}>Rename</Button> : null}
                  {canRevoke ? <Button size="sm" aria-label={`Revoke ${k.label}`} onClick={() => setRevoking(k)}>Revoke</Button> : null}</span> : null },
              ]} />}
        </Load>
      </Card>
      <Drawer open={renaming !== null} onOpenChange={(o) => { if (!o) setRenaming(null); }} title="Rename kiosk"
        footer={<><Button onClick={() => setRenaming(null)}>{t.cancel}</Button><Button variant="primary" busy={rename.isPending} disabled={!label.trim()} onClick={() => rename.mutate()}>{t.save}</Button></>}>
        <div className={s.stack}><Field label="Label" htmlFor="k-label"><input id="k-label" className={s.input} value={label} onChange={(e) => setLabel(e.target.value)} /></Field>
          {renameErr ? <div className={s.formErr} role="alert" style={{ margin: 0 }}>{renameErr}</div> : null}</div>
      </Drawer>
      <ConfirmDrawer open={revoking !== null} onClose={() => { setRevoking(null); setRevokeErr(null); }} title="Revoke kiosk" description={`${revoking?.label ?? ""} stops working at once. A scanner kiosk also triggers a new signing key.`}
        confirmLabel="Revoke kiosk" busyLabel="Revoking…" busy={revoke.isPending} error={revokeErr} onConfirm={() => revoke.mutate()} />
      <ConfirmDrawer open={rotate} onClose={() => { setRotate(false); setRotateErr(null); }} title="Rotate the check-in key" description="Members' QR codes and kiosks switch to a new signing key. Do this if a kiosk was lost or stolen."
        confirmLabel="Rotate key" busyLabel="Rotating…" busy={rot.isPending} error={rotateErr} onConfirm={() => rot.mutate()} />
    </Page>
  );
}

/* ---------- messaging ---------- */

export function MessagingPage() {
  const sc = useScope();
  const health = useQuery({ queryKey: sc.key("messaging-health"), queryFn: () => getHealth(sc.client), retry: false });
  // Messaging came with PR #32. If this server predates it, the health route is a 404 and the whole page is a "coming soon" card.
  if (health.error && (health.error as { status?: number }).status === 404) {
    return (
      <Page title={t.sections.messaging.title} description={t.sections.messaging.body} allow={section("messaging")}>
        <Card><EmptyState icon="chat" title="Messaging is coming soon" description="WhatsApp settings and templates appear here once messaging is connected on this server." action={<Button as={Link} to="/settings">Back to Settings</Button>} /></Card>
      </Page>
    );
  }
  return (
    <Page title={t.sections.messaging.title} description={t.sections.messaging.body} allow={section("messaging")}>
      <MessagingSettingsForm />
      <Card aria-label="Messaging health">
        <CardHeader title="Health" subtitle="What the last 24 hours looked like." />
        <Load query={health} what="messaging health" skeleton={<TableSkeleton rows={2} />}>
          {(h) => (
            <dl className={s.dl} style={{ padding: "0 16px 16px" }}>
              <dt>Sending</dt><dd>{h.paused ? "Paused" : "On"}</dd>
              <dt>Queued</dt><dd>{h.queued}</dd>
              <dt>Failed in 24h</dt><dd>{h.failuresLast24h.total}</dd>
              <dt>Opted out</dt><dd>{h.optedOut}</dd>
              <dt>Template language</dt><dd>{h.templateLanguage}{h.fallbackLanguage ? ` (fallback ${h.fallbackLanguage})` : ""}</dd>
              <dt>Last check</dt><dd>{h.lastTickAt ? fmtDateTime(h.lastTickAt, sc.ctx.timeZone, sc.ctx.locale) : "Never"}</dd>
            </dl>
          )}
        </Load>
      </Card>
      <ConnectionsCard />
      <TemplatesCard />
    </Page>
  );
}

function ConnectionsCard() {
  const sc = useScope();
  const q = useQuery({ queryKey: sc.key("messaging-connections"), queryFn: () => listConnections(sc.client), retry: false });
  return (
    <Card aria-label="WhatsApp numbers">
      <CardHeader title="WhatsApp numbers" subtitle="One per brand. Connecting a number is not done from the app yet." />
      <Load query={q} what="WhatsApp numbers" skeleton={<TableSkeleton rows={2} />}>
        {(list) => list.length === 0 ? <EmptyState compact icon="chat" title="No brands yet" /> : (
          <Table label="WhatsApp numbers" rows={list} rowKey={(c) => c.brandId}
            columns={[{ key: "b", header: "Brand", cell: (c) => c.brandName }, { key: "p", header: "Number", cell: (c) => c.phone ?? "—" }, { key: "s", header: "Status", cell: (c) => <Badge tone={c.status === "connected" ? "ok" : c.status === "degraded" ? "warn" : "neutral"}>{c.status.replace("_", " ")}</Badge> }]} />
        )}
      </Load>
    </Card>
  );
}

function TemplatesCard() {
  const sc = useScope();
  const q = useQuery({ queryKey: sc.key("messaging-templates"), queryFn: () => listTemplates(sc.client), retry: false });
  return (
    <Card aria-label="Message templates">
      <CardHeader title="Templates" subtitle="Approval status of each message." />
      <Load query={q} what="templates" skeleton={<TableSkeleton rows={4} />}>
        {(list) => list.length === 0 ? <EmptyState compact icon="chat" title="No templates yet" description="Templates appear once a WhatsApp number is connected." /> : (
          <Table label="Message templates" rows={list} rowKey={(x) => x.id} minWidth={520} maxHeight={360}
            columns={[{ key: "k", header: "Message", cell: (x) => x.templateKey }, { key: "l", header: "Language", cell: (x) => x.language },
              { key: "s", header: "Status", cell: (x) => <Badge tone={x.status === "approved" ? "ok" : x.status === "rejected" ? "crit" : "warn"}>{x.status}</Badge> }]} />
        )}
      </Load>
    </Card>
  );
}
